import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableHead, TableBody,
    TableRow, TableCell, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, CircularProgress,
    Alert, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import BusinessIcon from '@mui/icons-material/Business';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/api';
import ConfirmDialog from '../../components/common/ConfirmDialog';

export default function AdminIstas() {
    const [istas, setIstas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialog, setDialog] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [error, setError] = useState('');

    const [form, setForm] = useState({
        nom: '', code: '', description: '', dr: '',
        centre_formation: '', annee_formation: '', is_active: true,
    });

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const { data } = await adminAPI.getIstas();
            setIstas(data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleOpen = (ista = null) => {
        if (ista) {
            setEditing(ista.id);
            setForm({
                nom: ista.nom, code: ista.code, description: ista.description || '',
                dr: ista.dr || '', centre_formation: ista.centre_formation || '',
                annee_formation: ista.annee_formation || '', is_active: ista.is_active,
            });
        } else {
            setEditing(null);
            setForm({ nom: '', code: '', description: '', dr: '', centre_formation: '', annee_formation: '', is_active: true });
        }
        setError('');
        setDialog(true);
    };

    const handleSave = async () => {
        setError('');
        try {
            if (editing) {
                await adminAPI.updateIsta(editing, form);
                toast.success('ISTA modifié');
            } else {
                await adminAPI.createIsta(form);
                toast.success('ISTA créé');
            }
            setDialog(false);
            loadData();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.errors;
            setError(typeof msg === 'object' ? Object.values(msg).flat().join('. ') : msg || 'Erreur');
        }
    };

    const handleDelete = async () => {
        try {
            await adminAPI.deleteIsta(deleteId);
            toast.success('ISTA supprimé');
            setDeleteId(null);
            loadData();
        } catch (err) { toast.error('Erreur lors de la suppression'); }
    };

    const filtered = istas.filter(i =>
        !search ||
        i.nom.toLowerCase().includes(search.toLowerCase()) ||
        i.code.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>Gérer les ISTAs</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        size="small" placeholder="Rechercher..."
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        }}
                    />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}
                        sx={{ background: 'linear-gradient(135deg, #1a237e, #283593)', textTransform: 'none', borderRadius: 2 }}>
                        Nouvel ISTA
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <div className="responsive-table-wrapper">
                    <Table>
                    <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>DR</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Centre</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Année</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>RH</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Filières</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Offres</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map(i => (
                            <TableRow key={i.id} hover>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BusinessIcon color="action" fontSize="small" />
                                        <Chip label={i.code} size="small" variant="outlined" />
                                    </Box>
                                </TableCell>
                                <TableCell><Typography variant="body2" fontWeight={600}>{i.nom}</Typography></TableCell>
                                <TableCell>{i.dr || '—'}</TableCell>
                                <TableCell>{i.centre_formation || '—'}</TableCell>
                                <TableCell>{i.annee_formation || '—'}</TableCell>
                                <TableCell><Chip label={i.rh_users_count || 0} size="small" color="primary" variant="outlined" /></TableCell>
                                <TableCell><Chip label={i.filieres_count || 0} size="small" color="secondary" variant="outlined" /></TableCell>
                                <TableCell><Chip label={i.job_offers_count || 0} size="small" color="info" variant="outlined" /></TableCell>
                                <TableCell>
                                    <Chip label={i.is_active ? 'Actif' : 'Inactif'} color={i.is_active ? 'success' : 'default'} size="small" />
                                </TableCell>
                                <TableCell>
                                    <IconButton size="small" color="primary" onClick={() => handleOpen(i)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" color="error" onClick={() => setDeleteId(i.id)}><DeleteIcon fontSize="small" /></IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    </Table>
                </div>
            </Paper>

            {/* Create / Edit Dialog */}
            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? 'Modifier l\'ISTA' : 'Nouvel ISTA'}</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
                        <TextField fullWidth label="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                        <TextField fullWidth label="Code (ex: ISTA-HS)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
                    </Box>
                    <TextField fullWidth multiline rows={2} label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField fullWidth label="Direction Régionale (DR)" value={form.dr} onChange={(e) => setForm({ ...form, dr: e.target.value })} />
                        <TextField fullWidth label="Centre de Formation" value={form.centre_formation} onChange={(e) => setForm({ ...form, centre_formation: e.target.value })} />
                    </Box>
                    <TextField fullWidth label="Année de Formation (ex: 2025-2026)" value={form.annee_formation} onChange={(e) => setForm({ ...form, annee_formation: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)} sx={{ textTransform: 'none' }}>Annuler</Button>
                    <Button variant="contained" onClick={handleSave}
                        sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #1a237e, #283593)' }}>
                        {editing ? 'Modifier' : 'Créer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog open={!!deleteId} title="Supprimer l'ISTA"
                message="Attention: les filières, modules et offres liés seront dissociés. Continuer ?"
                onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
        </Box>
    );
}
